import React from 'react';
import { ChartLine, Buildings, Receipt, CheckCircle, Calculator } from '@phosphor-icons/react';

export const FinanceSummaryWidget = ({ wrapStyle, finance, language }) => {
  if (!finance) return null;
  return (
    <div style={wrapStyle}>
      <div className="card">
        <div className="card-header" style={{ backgroundColor: '#E3F2FD' }}>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1565C0' }}>
            <ChartLine size={20} weight="bold" /> {language === 'en' ? 'Financial Summary' : 'Synthese Financiere'}
          </h3>
        </div>
        <div style={{ padding: '24px' }}>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="stat-card" data-testid="finance-cumul-chantiers">
              <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Buildings size={16} /> {language === 'en' ? 'Total Sites HT' : 'Cumul Chantiers HT'}</div>
              <div className="stat-value" style={{ fontSize: '1.5rem' }}>{finance.cumul_chantiers_ht.toLocaleString('fr-FR')}&euro;</div>
            </div>
            <div className="stat-card" data-testid="finance-depenses-globales">
              <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Receipt size={16} /> {language === 'en' ? 'Total Expenses HT' : 'Depenses Globales HT'}</div>
              <div className="stat-value" style={{ fontSize: '1.5rem', color: '#C62828' }}>{finance.depenses_globales_ht.toLocaleString('fr-FR')}&euro;</div>
            </div>
            <div className="stat-card" data-testid="finance-facture">
              <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><CheckCircle size={16} /> {language === 'en' ? 'Invoiced HT' : 'Montant Facture HT'}</div>
              <div className="stat-value" style={{ fontSize: '1.5rem', color: '#2E7D32' }}>{finance.montant_facture_ht.toLocaleString('fr-FR')}&euro;</div>
            </div>
            <div className="stat-card" data-testid="finance-reste-facturer">
              <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Calculator size={16} /> {language === 'en' ? 'Remaining to Invoice' : 'Reste a Facturer HT'}</div>
              <div className="stat-value" style={{ fontSize: '1.5rem', color: '#EF6C00' }}>{finance.reste_a_facturer_ht.toLocaleString('fr-FR')}&euro;</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CaAnnuelWidget = ({ wrapStyle, finance, language }) => {
  if (!finance) return null;
  const annee = finance.annee_courante || new Date().getFullYear();
  const total = finance.ca_annee_courante_ht || 0;
  return (
    <div style={wrapStyle}>
      <div className="card" data-testid="ca-annuel-widget">
        <div style={{
          padding: '22px 26px',
          background: 'linear-gradient(135deg, #002FA7 0%, #3D5AFE 55%, #5C7EFF 100%)',
          color: 'white',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ChartLine size={26} weight="bold" />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '1.2px', opacity: 0.85, fontWeight: '700' }}>
                {language === 'en' ? `Total Revenue ${annee}` : `Chiffre d'affaires total ${annee}`}
              </div>
              <div style={{ fontSize: '2.1rem', fontWeight: '800', marginTop: '2px', lineHeight: 1 }} data-testid="ca-annuel-amount">
                {total.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} &euro;
                <span style={{ fontSize: '0.85rem', fontWeight: '500', opacity: 0.9, marginLeft: '8px' }}>HT</span>
              </div>
              <div style={{ fontSize: '0.78rem', opacity: 0.9, marginTop: '4px' }}>
                {language === 'en' ? 'Cumulative invoiced across all projects this year' : 'Cumul facturé sur l\'ensemble des chantiers cette année'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CaMensuelWidget = ({ wrapStyle, finance, language, getMonthLabel }) => {
  if (!finance) return null;
  return (
    <div style={wrapStyle}>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ChartLine size={18} /> {language === 'en' ? 'Monthly Revenue' : "Chiffre d'affaires realise par mois"}
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" data-testid="ca-mensuel-table">
            <thead><tr>{finance.ca_mensuel.map(item => <th key={item.month} style={{ textAlign: 'center', minWidth: '80px' }}>{getMonthLabel(item.month)}</th>)}</tr></thead>
            <tbody><tr>{finance.ca_mensuel.map(item => <td key={item.month} style={{ textAlign: 'center', fontWeight: '600', color: item.amount > 0 ? '#2E7D32' : '#5E5F66' }}>{item.amount.toLocaleString('fr-FR')}&euro;</td>)}</tr></tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const TvaMensuelleWidget = ({ wrapStyle, finance, language, getMonthLabel }) => {
  if (!finance) return null;
  return (
    <div style={wrapStyle}>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calculator size={18} /> {language === 'en' ? 'Monthly VAT' : 'TVA a payer par mois'}
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" data-testid="tva-mensuel-table">
            <thead><tr>{finance.tva_mensuelle.map(item => <th key={item.month} style={{ textAlign: 'center', minWidth: '80px' }}>{getMonthLabel(item.month)}</th>)}</tr></thead>
            <tbody><tr>{finance.tva_mensuelle.map(item => <td key={item.month} style={{ textAlign: 'center', fontWeight: '600', color: item.amount > 0 ? '#C62828' : item.amount < 0 ? '#2E7D32' : '#5E5F66' }}>{item.amount.toLocaleString('fr-FR')}&euro;</td>)}</tr></tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
